/**
 * ADR-0013 — Customer Balance Owner.
 *
 * Read-SSOT: customerDebts[].remainingDebt (persistiert).
 * Credit-Sale-Deltas: saleBalance.js (nur expliziter Kredit/Rest).
 */

/**
 * @param {Array} [customerDebts]
 * @param {string|number} customerId
 * @returns {number}
 */
export function resolveCustomerBalance(customerDebts = [], customerId) {
  if (customerId == null || customerId === '') return 0;
  const debt = (customerDebts || []).find(
    (d) => d && String(d.customerId) === String(customerId)
  );
  return Math.max(0, Number(debt?.remainingDebt) || 0);
}

/**
 * Portfolio créances (Accounts Receivable) für Bilanz.
 * @param {Array} [customerDebts]
 * @returns {number}
 */
export function summarizeAccountsReceivable(customerDebts = []) {
  if (!Array.isArray(customerDebts)) return 0;
  return customerDebts.reduce((sum, debt) => {
    const remaining = Number(debt?.remainingDebt) || 0;
    return sum + Math.max(0, remaining);
  }, 0);
}

/**
 * Recalc aus Invoice-Zeilen (für Rebuild/Sync — noch kaum Caller).
 * @param {{ invoices?: Array, creditLimit?: number }} params
 */
export function computeCustomerDebtTotals({ invoices = [], creditLimit = 0 } = {}) {
  const totalDebt = (invoices || []).reduce(
    (sum, inv) => sum + (Number(inv?.totalAmount) || 0),
    0
  );
  const paidAmount = (invoices || []).reduce(
    (sum, inv) => sum + (Number(inv?.paidAmount) || 0),
    0
  );
  const remainingDebt = totalDebt - paidAmount;
  const creditUsed = remainingDebt;
  return {
    totalDebt,
    paidAmount,
    remainingDebt,
    creditUsed,
    creditAvailable: Math.max(0, (Number(creditLimit) || 0) - creditUsed)
  };
}

/**
 * Credit-Limit Gate (für Credit-Sales / POS).
 * @param {{ creditLimit?: number, creditUsed?: number, remainingDebt?: number }} debtRecord
 * @param {number} [additionalAmount]
 */
export function assessCreditCapacity(debtRecord = {}, additionalAmount = 0) {
  const creditLimit = Number(debtRecord.creditLimit) || 0;
  const creditUsed =
    Number(debtRecord.creditUsed) ||
    Math.max(0, Number(debtRecord.remainingDebt) || 0);
  const additional = Math.max(0, Number(additionalAmount) || 0);
  const projectedUsed = creditUsed + additional;
  const available = Math.max(0, creditLimit - creditUsed);
  const ratio = creditLimit > 0 ? projectedUsed / creditLimit : 0;
  return {
    creditLimit,
    creditUsed,
    available,
    projectedUsed,
    withinLimit: creditLimit <= 0 ? true : projectedUsed <= creditLimit,
    warning: creditLimit > 0 && ratio >= 0.8
  };
}

/**
 * POS/Checkout: Limit + aktuelle Créance für einen Kunden auflösen.
 * Limit: debt.creditLimit, sonst customer.creditLimit (0 = kein Gate).
 */
export function resolveCreditCapacityForCustomer({
  customerId,
  customers = [],
  customerDebts = [],
  additionalAmount = 0
} = {}) {
  if (customerId == null || customerId === '') {
    return assessCreditCapacity({ creditLimit: 0, remainingDebt: 0 }, additionalAmount);
  }
  const id = String(customerId);
  const customer = (customers || []).find(
    (c) => c && (String(c.customerId) === id || String(c.id) === id)
  );
  const debt = (customerDebts || []).find((d) => d && String(d.customerId) === id);
  const creditLimit =
    Number(debt?.creditLimit ?? customer?.creditLimit ?? customer?.credit?.limit) || 0;
  const remainingDebt = resolveCustomerBalance(customerDebts, customerId);
  return assessCreditCapacity({ creditLimit, remainingDebt }, additionalAmount);
}

export {
  resolveSaleUnpaidAmount,
  planCustomerBalanceDeltaFromSale,
  applyCustomerBalanceDelta
} from './saleBalance';
export {
  collectCustomerDebtPayments,
  filterCustomerDebtPaymentsInRange,
  resolveDebtPaymentInstant,
  planCustomerDebtPayment,
  applyCustomerDebtPayment
} from './debtCollection';
export { recordCustomerDebtPayment } from './recordCustomerDebtPayment';
